import { logWithState } from '@gimlite/common';
import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useStoreUIV2 } from '../hooks/useStore.hook';
import { RootStoreMobX } from './root.store';

type WizardType = {
  id: string;
  component: React.ComponentType<any>;
};

type WizardStoreType = any;

export type WizardInstance = {
  instanceId: string;
  wizard: WizardType;
  store?: WizardStoreType;
};

export const WIZARD_REOPEN_DELAY = 100;
export const WIZARD_CLOSE_ANIMATION_DELAY = 1000;

export type WizardStoreContextType = {
  selected: { wizard: WizardType; store?: WizardStoreType };
  registry: Array<WizardType>;
};

export class WizardStoreMobX {
  rootStore: RootStoreMobX;
  @observable public load: boolean = false;
  @observable public instances: Map<string, WizardInstance> = new Map();
  @observable public nextSelected: WizardStoreContextType['selected'] | null =
    null;
  @observable public registry: WizardStoreContextType['registry'] = [];

  constructor(rootStore: RootStoreMobX) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  private generateInstanceId(): string {
    return `wizard-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  @computed get hasInstances(): boolean {
    return this.instances.size > 0;
  }

  @computed get instancesCount(): number {
    return this.instances.size;
  }

  @computed get registryCount(): number {
    return this.registry.length;
  }

  @computed get instancesArray(): WizardInstance[] {
    return Array.from(this.instances.values());
  }

  @computed get selected(): Array<{
    wizard: WizardType;
    store?: WizardStoreType;
  }> {
    return this.instancesArray.map((instance) => ({
      wizard: instance.wizard,
      store: instance.store,
    }));
  }

  @action public init({
    registry,
  }: {
    registry: WizardStoreContextType['registry'];
  }) {
    runInAction(() => {
      this.load = true;
      this.registry = [...this.registry, ...registry];
    });
  }

  @action public openOrUpdate(item: {
    id: WizardType['id'];
    store?: WizardStoreType;
  }): string | null {
    const { id, store } = item;

    if (!this.load) return null;

    const wizardDef = this.registry.find((w) => w.id === id);
    if (!wizardDef) return null;

    const existingInstance = Array.from(this.instances.values()).find(
      (instance) => instance.wizard.id === id,
    );

    if (existingInstance) {
      runInAction(() => {
        this.instances.set(existingInstance.instanceId, {
          ...existingInstance,
          store: { ...existingInstance.store, ...store },
        });
      });
      return existingInstance.instanceId;
    }

    return this._createInstance(wizardDef, store);
  }

  @action public openNew(item: {
    id: WizardType['id'];
    store?: WizardStoreType;
  }): string | null {
    const { id, store } = item;

    if (!this.load) return null;

    const wizardDef = this.registry.find((w) => w.id === id);
    if (!wizardDef) return null;

    return this._createInstance(wizardDef, store);
  }

  private _createInstance(
    wizardDef: WizardType,
    store?: WizardStoreType,
  ): string {
    const instanceId = this.generateInstanceId();

    runInAction(() => {
      this.instances.set(instanceId, {
        instanceId,
        wizard: wizardDef,
        store,
      });
    });

    return instanceId;
  }

  @action public close(item: { id: WizardType['id'] }) {
    runInAction(() => {
      Array.from(this.instances.entries()).forEach(([instanceId, instance]) => {
        if (instance.wizard.id === item.id) {
          this.instances.delete(instanceId);
        }
      });
    });
  }

  @action public closeInstance(instanceId: string) {
    runInAction(() => {
      this.instances.delete(instanceId);
    });
  }

  @action public closeAll() {
    runInAction(() => {
      this.instances.clear();
    });
  }

  @action public nextSelect(item: {
    id: WizardType['id'];
    store?: WizardStoreType['store'];
  }) {
    const { id, store } = item;

    if (this.load) {
      const selectedWizard = this.registry.find((item) => item.id === id);

      if (!!selectedWizard) {
        runInAction(() => {
          this.nextSelected = {
            wizard: selectedWizard,
            store,
          };
        });
      }
    }
  }

  @action public updateStore(instanceId: string, record: Record<string, any>) {
    const instance = this.instances.get(instanceId);
    if (instance) {
      runInAction(() => {
        this.instances.set(instanceId, {
          ...instance,
          store: { ...instance.store, ...record },
        });
      });
    }
  }

  @action public clear() {
    this.closeAll();
  }

  public getInstance(instanceId: string): WizardInstance | undefined {
    return this.instances.get(instanceId);
  }

  public getInstancesByWizardId(wizardId: string): WizardInstance[] {
    return Array.from(this.instances.values()).filter(
      (instance) => instance.wizard.id === wizardId,
    );
  }

  public filterBaseStore(store: WizardStoreType): WizardStoreType {
    return Object.fromEntries(
      Object.entries(store || {}).filter(([key]) => key.startsWith('on')),
    );
  }
}

export const ConfigWizard = observer(
  ({
    children,
    registry,
  }: {
    children: React.ReactNode;
    registry: WizardStoreContextType['registry'];
  }) => {
    const { WizardStore } = useStoreUIV2();

    useEffect(() => {
      if (!WizardStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigWizard init' });
        WizardStore.init({ registry });
      }

      if (WizardStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigWizard load' });
      }
    }, [WizardStore.load]);

    return WizardStore.load ? children : null;
  },
);
